/* eslint-disable prefer-destructuring,jsx-a11y/control-has-associated-label,import/prefer-default-export */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/display-name */
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';

import { SETTINGS_SUPPORTED_COUNTRIES_QUERY_KEY } from '../../../../utilities/sharedQueries';
import {
  ChevronDown,
  Control,
  Dropdown,
  IndicatorsContainer,
  MenuList,
  Option,
  Placeholder,
  SelectMenu,
  ValueContainer,
} from '../../../../components/LargeList';
import { ACCOUNT_REGIONS } from '../../../../utilities/constants';
import { US_COUNTRY_ALPHA } from '../../../../screens/Onboarding/constants';
import { dynamicAssets } from '../../../../utilities/helpers';
import { useSetting } from '../../../../utilities/sharedHooks/settings';

export const SelectCountryDropdown = ({
  value,
  placeholder,
  name,
  getOptionLabel,
  isOptionSelected,
  onChange,
  handleBlur,
  clearError,
  className,
  optionName,
  prefix,
  fieldsInfo,
  containerClass,
  dropDownParentClass,
  withValueImageFlag,
  readOnly,
  flagValue,
  isPhoneNumber,
  useFilteredCountries,
  region,
  defaultValue,
  onFocus,
  filterCountryIds,
  showAllCountriesNoRestrictions,
}) => {
  const { t: translate } = useTranslation();
  const { data: _countries, isLoading: isLoadingCountries } = useQuery(
    SETTINGS_SUPPORTED_COUNTRIES_QUERY_KEY
  );
  const [allowedSignUpCountries] = useSetting('allowed_signup_countries', '', true, false);

  const [restrictedPhoneNumbersEnabled] = useSetting(
    'app_features',
    'feature_restrict_phone_numbers_from_unsupported_countries',
    false,
    false
  );

  const _countriesWithAllowedSignUpCountries = useMemo(() => {
    if (
      restrictedPhoneNumbersEnabled &&
      allowedSignUpCountries &&
      !showAllCountriesNoRestrictions
    ) {
      return allowedSignUpCountries
        ?.filter((item) => !filterCountryIds?.includes(item.id))
        ?.sort((a, b) => a.displayName.localeCompare(b.displayName));
    }
    return _countries?.filter((item) => !filterCountryIds?.includes(item.id));
  }, [
    _countries,
    allowedSignUpCountries,
    restrictedPhoneNumbersEnabled,
    filterCountryIds,
    showAllCountriesNoRestrictions,
  ]);

  const [displayLabel, setDisplayLabel] = useState(false);

  const [displayPlaceholder, setDisplayPlaceholder] = useState(false);

  const MAX_DISPLAY_NAME_SYMBOLS = 33;
  const countries = useMemo(() => {
    if (_countriesWithAllowedSignUpCountries) {
      return _countriesWithAllowedSignUpCountries.filter((country) => {
        const displayName = country.displayName;
        if (!region || (region && region !== ACCOUNT_REGIONS.UK_OR_EU)) {
          country.displayName =
            displayName.length > MAX_DISPLAY_NAME_SYMBOLS
              ? `${displayName.substr(0, MAX_DISPLAY_NAME_SYMBOLS)}...`
              : displayName;
          return country;
        }

        if (
          region &&
          region === ACCOUNT_REGIONS.UK_OR_EU &&
          country.iso3166alpha3 !== US_COUNTRY_ALPHA
        ) {
          country.displayName =
            displayName.length > MAX_DISPLAY_NAME_SYMBOLS
              ? `${displayName.substr(0, MAX_DISPLAY_NAME_SYMBOLS)}...`
              : displayName;
          return country;
        }
        return false;
      });
    }

    return [];
  }, [_countriesWithAllowedSignUpCountries, region]);

  const filteredCountries = useMemo(() => {
    if (countries) {
      return countries.reduce((accumulator, country) => {
        const indexItem = accumulator.findIndex((item) => item.displayName === country.displayName);
        if (indexItem < 0) {
          return [...accumulator, country];
        }
        return accumulator;
      }, []);
    }
    return [];
  }, [countries]);

  const [isOpened, setIsOpened] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);

  const [selectedImageValue, setSelectedImageValue] = useState(flagValue);

  useEffect(() => {
    setSelectedValue(value);
    if (flagValue) {
      setSelectedImageValue(flagValue);
    }
  }, [value, flagValue]);

  let searchInput;
  const countryCode = useRef();

  const OPTION_HEIGHT = 44;
  const ROWS = 4;

  const filterOption = ({ data }, string) => {
    if (string === '') return true;
    const str = string.replace(prefix, '').toLowerCase();
    return (
      data.displayName.toLowerCase().indexOf(str) !== -1 ||
      data.callingCode.toLowerCase().indexOf(str) !== -1
    );
  };

  const toggleFormGroupHeight = (e) => {
    setIsOpened(!isOpened);
    if (!e.target) {
      e.target = document.getElementsByClassName('select_menu-container')[0];
    }
    const parentFormControl = e.target.closest('.form_group-container');
    const parentModal = e.target.closest('.modal-content');
    if (parentModal) {
      if (!isOpened) {
        parentModal.style.minHeight = '51.5rem';
        // this comment is for fix COB-241
        // parentModal.style.height = 'auto';
      } else {
        parentModal.style.minHeight = '';
        parentModal.style.height = '51.5rem';
      }
    }
    if (!isOpened && parentFormControl) {
      parentFormControl.style.height = `${parentFormControl.offsetHeight + OPTION_HEIGHT * ROWS}px`;
    } else if (parentFormControl) {
      parentFormControl.style.height = 'auto';
    }
  };

  const onSelectChange = (e, updateHight = true) => {
    onChange(e, name);
    if (updateHight) {
      toggleFormGroupHeight(e);
    }
    countryCode.current.value = `${prefix}${e[optionName]}`;
    setSelectedValue(e[optionName]);
    setSelectedImageValue(e?.iso3166alpha2);
  };

  useEffect(() => {
    if (defaultValue && filteredCountries && filteredCountries.length > 0) {
      const defaultCountry = filteredCountries.find((item) => item.iso3166alpha3 === defaultValue);
      if (defaultCountry) {
        onSelectChange(defaultCountry, false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue, filteredCountries]);

  const handleFocus = (e) => {
    clearError(e);
    setDisplayLabel(false);
  };

  const handleInputBlur = () => {
    setDisplayLabel(false);
  };

  const Input = (props) => (
    <>
      <input
        id={props.id}
        onFocus={props.onFocus}
        onChange={(e) => {
          e.target.defaultValue = props.selectProps.value;
          props.onChange(e);
        }}
        onBlur={handleInputBlur}
        className="form-control-search bg-lighter border-0"
        autoComplete="off"
        placeholder={translate('search_country_name')}
        style={{ height: '2.5rem', lineHeight: '2.5rem' }}
        ref={(e) => {
          if (e) {
            countryCode.current.blur();
            searchInput = e.target || e;
          }
        }}
        readOnly={readOnly}
      />
      <i
        className="icon-search icon-search-other text-shadow"
        role="button"
        aria-label="Search"
        tabIndex="-1"
      />
    </>
  );

  Input.propTypes = {
    id: PropTypes.string.isRequired,
    onFocus: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    selectProps: PropTypes.oneOfType([PropTypes.object]).isRequired,
  };

  const triggerInput = (e) => {
    const elm = e.target || e;
    try {
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
      ).set;
      nativeInputValueSetter.call(searchInput, elm.value);
      const event = new Event('input', { bubbles: true });
      setSelectedValue(elm.value);
      searchInput.dispatchEvent(event);
    } catch (err) {
      return false;
    }
    return true;
  };

  return (
    <>
      <Dropdown
        isOpen={isOpened}
        onClose={toggleFormGroupHeight}
        dropDownParentClass={dropDownParentClass}
        isPhoneNumber={isPhoneNumber}
        target={
          <>
            {withValueImageFlag && selectedImageValue && (
              <img
                src={dynamicAssets(`./assets/images/flags/1x1/${selectedImageValue}.svg`)}
                className="rounded-extra position-absolute top-50 translate-middle-y"
                width="15"
                height="15"
                alt="flag"
              />
            )}
            <input
              autoComplete="off"
              type="text"
              className={`bg-transparent w-100 my-0 ${
                withValueImageFlag && selectedImageValue
                  ? 'px-3 onboarding-phone-number-form-input'
                  : 'text-indent'
              } ${isPhoneNumber ? 'form-control border-0 shadow-none' : 'form-control-input'} ${
                readOnly ? 'pe-1' : ''
              }`}
              placeholder={placeholder}
              defaultValue={`${prefix}${selectedValue}`}
              required
              name={name}
              onFocus={() => {
                setDisplayPlaceholder(true);
                onFocus();
              }}
              onBlur={() => setDisplayPlaceholder(false)}
              onChange={triggerInput}
              onClick={(e) => {
                if (!readOnly) {
                  toggleFormGroupHeight(e);
                }
              }}
              ref={(e) => {
                if (e) {
                  countryCode.current = e;
                }
              }}
              readOnly
              // style={{
              //   textIndent: withValueImageFlag && selectedImageValue ? '0.6rem' : '0rem',
              // }}
            />
            {displayPlaceholder && <label className="floating-label">{placeholder}</label>}
            {!readOnly && (
              <i
                tabIndex={0}
                className={`${
                  isOpened ? 'text-primary' : 'text-shadow'
                } position-absolute top-50 end-0 translate-middle-y`}
                role="button"
                onClick={(e) => {
                  if (!readOnly) {
                    toggleFormGroupHeight(e);
                  }
                  onFocus();
                }}
                onKeyDown={() => {}}>
                <ChevronDown
                  style={{
                    transform: `rotate(${isOpened ? '180deg' : '0deg'})`,
                  }}
                />
              </i>
            )}
            {(displayLabel || value.length > 0) && (
              <label className="floating-label">{placeholder}</label>
            )}
            <label className="error-label">{translate('input_required_error_label')}</label>
          </>
        }>
        <Select
          autoFocus
          backspaceRemovesValue={false}
          filterOption={filterOption}
          components={{
            IndicatorSeparator: null,
            MenuList,
            Option,
            Menu: SelectMenu,
            Input,
            Placeholder,
            ValueContainer,
            Control,
            IndicatorsContainer,
          }}
          controlShouldRenderValue={false}
          hideSelectedOptions={false}
          isClearable={false}
          isLoading={isLoadingCountries}
          menuIsOpen
          onChange={onSelectChange}
          options={useFilteredCountries ? filteredCountries : countries}
          tabSelectsValue={false}
          value={selectedValue}
          className={className}
          onBlur={(e) => {
            handleBlur(e);
            toggleFormGroupHeight(e);
          }}
          onFocus={handleFocus}
          getOptionLabel={getOptionLabel}
          isOptionSelected={isOptionSelected}
          fieldsInfo={fieldsInfo}
          containerClass={containerClass}
        />
      </Dropdown>
    </>
  );
};

SelectCountryDropdown.propTypes = {
  value: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  name: PropTypes.string.isRequired,
  handleBlur: PropTypes.func.isRequired,
  clearError: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  isOptionSelected: PropTypes.func.isRequired,
  getOptionLabel: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  className: PropTypes.string,
  optionName: PropTypes.string.isRequired,
  prefix: PropTypes.string.isRequired,
  fieldsInfo: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])).isRequired,
  containerClass: PropTypes.string,
  dropDownParentClass: PropTypes.string,
  withValueImageFlag: PropTypes.bool,
  readOnly: PropTypes.bool,
  isPhoneNumber: PropTypes.bool,
  showAllCountriesNoRestrictions: PropTypes.bool,
  useFilteredCountries: PropTypes.bool,
  flagValue: PropTypes.string,
  region: PropTypes.string,
  defaultValue: PropTypes.string,
  filterCountryIds: PropTypes.arrayOf(PropTypes.string),
};

SelectCountryDropdown.defaultProps = {
  containerClass: 'w-100 position-absolute start-0 bg-lighter rounded shadow-lg p-3 mt-2',
  className: '',
  dropDownParentClass: 'w-100 pe-3',
  withValueImageFlag: false,
  readOnly: false,
  isPhoneNumber: false,
  flagValue: '',
  useFilteredCountries: false,
  showAllCountriesNoRestrictions: false,
  placeholder: null,
  region: null,
  defaultValue: null,
  filterCountryIds: [],
  onFocus: () => {},
};
